import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core'
import { switchMap, takeUntil, filter } from 'rxjs/operators'
import { interval } from 'rxjs'
import { Router, NavigationEnd } from '@angular/router'

import { MessengerApiService } from '@app/modules/client/messenger/services/messenger-api.service'
import { ChatSocketService } from '@app/modules/client/messenger/services/chat-socket.service'
import { DestroyService } from '@app/core/services/destroy.service'
import { ChatModel, ChatSocketEventEnum, ChatSocketResponse, UserChannelsResponse } from '@shared/models/messenger/messenger.model'
import { MessengerService } from '../../services/messenger.service'
import { AuthService } from '@app/modules/auth/auth.service'
import { PaginationData } from '@app/shared/models/pages-data.model'
import { SettingsService } from '@app/shared/common-components/settings/settings.service'
import { sameId } from '@core/helpers/same-id'

@Component({
  selector: 'lin-chat-toolbar',
  templateUrl: './chat-toolbar.component.html',
  styleUrls: ['./chat-toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DestroyService],
  standalone: false,
})
export class ChatToolbarComponent implements OnInit {
  @Input() isNotificationSound: boolean

  private audio: HTMLAudioElement | null = null
  private privateChannel: string
  private teamChannel: string
  private INTERVAL = 60000
  private currentUser = this.authService.getCurrentUser()

  constructor(
    public readonly messengerService: MessengerService,
    private readonly messengerApiService: MessengerApiService,
    private readonly chatSocketService: ChatSocketService,
    private readonly destroyService: DestroyService,
    private readonly authService: AuthService,
    private readonly settingsService: SettingsService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.initConnection()
    this.initUnreadCount()
    this.subscribePrivateChannel()
    this.subscribeTeamChannel()
    this.pingOnlineUser()
    this.initRouteAwareAudioLoad()
  }

  private initRouteAwareAudioLoad(): void {
    this.router.events
      .pipe(
        takeUntil(this.destroyService),
        filter((event) => event instanceof NavigationEnd),
      )
      .subscribe((event: NavigationEnd) => {
        const isMapPage = event.urlAfterRedirects.includes('/client/map')
        if (!isMapPage && this.isNotificationSound) {
          this.lazyInitAudio()
        }
      })
  }

  private initConnection(): void {
    this.messengerApiService
      .getUserChannels()
      .pipe(takeUntil(this.destroyService))
      .subscribe((channels: UserChannelsResponse) => {
        this.privateChannel = channels.private
        this.teamChannel = channels.team
        this.chatSocketService.connect(channels)
      })
  }

  private initUnreadCount(): void {
    this.messengerApiService
      .getChatList({ isUnread: 1 })
      .pipe(takeUntil(this.destroyService))
      .subscribe(({ additionalFields: { totalUnreadCount } }: PaginationData<ChatModel[]>) => {
        this.messengerService.setUnreadCount(totalUnreadCount)
      })
  }

  private subscribePrivateChannel(): void {
    this.chatSocketService.stateCentrifuge$
      .pipe(switchMap(this.chatSocketService.connectPrivateChannel), takeUntil(this.destroyService))
      .subscribe(this.privateChannelSubscribe)
  }

  private subscribeTeamChannel(): void {
    this.chatSocketService.stateCentrifuge$
      .pipe(switchMap(this.chatSocketService.connectTeamChannel), takeUntil(this.destroyService))
      .subscribe()
  }

  private pingOnlineUser(): void {
    this.chatSocketService.stateCentrifuge$
      .pipe(
        switchMap(() => this.messengerApiService.postUserOnline()),
        switchMap(() => interval(this.INTERVAL)),
        switchMap(() => this.messengerApiService.postUserOnline()),
        takeUntil(this.destroyService),
      )
      .subscribe()
  }

  private privateChannelSubscribe = (response: ChatSocketResponse): void => {
    const {
      data: {
        event,
        data: { chat, message },
      },
    } = response

    if (!chat || !message) return

    if (event === ChatSocketEventEnum.EVENT_MESSAGE_SENT) {
      if (!sameId(message.user.id, this.currentUser.id) && !message.isRead) {
        this.messengerService.setUnreadCount(undefined, true)

        if (this.isNotificationSound && this.audio) {
          this.audio.play()
        }
      }
    }
  }

  private lazyInitAudio(): void {
    if (this.audio) return

    this.audio = new Audio()
    this.audio.src = '/assets/audio/juntos-607.mp3'
    this.audio.load()

    this.settingsService
      .getUserSoundVolumeSetting(this.currentUser.id)
      .pipe(takeUntil(this.destroyService))
      .subscribe((setting) => {
        if (setting?.value === 'Loud') {
          this.audio!.src = '/assets/audio/mixkit-interface-option-select-2573.wav'
          this.audio!.volume = 1.0
        } else {
          this.audio!.src = '/assets/audio/juntos-607.mp3'
          this.audio!.volume = 0.3
        }

        this.audio!.load()
      })
  }
}
